import { defineStore } from "pinia";
import apiClient from "@/axiosConfig";

const STORAGE_COMPANY_ID = "mkzugferd.currentCompanyId";
const STORAGE_LOCALE = "mkzugferd.locale";
const STORAGE_THEME = "mkzugferd.theme";
function normalizeTheme(theme) {
  const value = (theme || "").trim();
  return value.length ? value : null;
}

function normalizeLocale(locale) {
  const value = (locale || "").trim().toLowerCase();
  return value.length ? value : null;
}

export const useUserStore = defineStore("user", {
  state: () => ({
    me: null,
    accessState: "Unavailable",
    logoObjectUrl: null,
    developerOptions: null,
    personalAccessTokens: [],
  }),
  getters: {
    userId: (state) => state.me?.userId || null,
    displayName: (state) => state.me?.displayName || null,
    email: (state) => state.me?.email || null,
    preferredUserName: (state) => state.me?.preferredUserName || null,
    currentCompanyId: (state) => state.me?.currentCompanyId || null,
    companies: (state) => state.me?.companies || [],
    roles: (state) => state.me?.roles || [],
    permissions: (state) => state.me?.permissions || [],
    apiUsage: (state) => state.me?.apiUsage || null,
    isSystemAdmin: (state) => (state.me?.roles || []).includes("SystemAdmin"),
    isCompanyAdmin: (state) => (state.me?.roles || []).includes("CompanyAdmin"),
    isDeveloperMenuVisible: (state) => (state.me?.roles || []).includes("SystemAdmin"),
    isAwaitingAssignment: (state) => ["AssignmentPending", "CompanySelectionInvalid"].includes(state.accessState),
    hasPermission: (state) => (permissionCode) => Boolean(permissionCode) && (state.me?.permissions || []).includes(permissionCode),
    locale: (state) => state.me?.locale || null,
    theme: (state) => state.me?.theme || null,
    currentCompany: (state) => {
      const id = state.me?.currentCompanyId;
      return (state.me?.companies || []).find((c) => c.id === id) || null;
    },
  },
  actions: {
    async loadMe() {
      this.developerOptions = null;
      this.personalAccessTokens = [];
      try {
        const response = await apiClient.get("/user/me");
        this.me = response?.data || null;
      } catch (error) {
        const code = error?.response?.data?.code;
        this.me = null;
        this.accessState = code === "company_selection_invalid" ? "CompanySelectionInvalid"
          : code === "application_access_suspended" ? "Suspended" : "Unavailable";
        throw error;
      }

      this.accessState = this.me?.accessState || ((this.me?.permissions || []).length ? "Active" : "AssignmentPending");
      if (this.me?.currentCompanyId) {
        localStorage.setItem(STORAGE_COMPANY_ID, this.me.currentCompanyId);
      }
      if (this.me?.locale) {
        localStorage.setItem(STORAGE_LOCALE, this.me.locale);
      }
      if (this.me?.theme) {
        localStorage.setItem(STORAGE_THEME, this.me.theme);
      }

      if (this.accessState === "Active") await this.refreshCompanyLogo();
      return this.me;
    },

    async loadDeveloperOptions() {
      if (!this.isDeveloperMenuVisible) {
        this.developerOptions = null;
        return null;
      }

      const response = await apiClient.get("/user/developer/options");
      this.developerOptions = response?.data || null;
      return this.developerOptions;
    },

    async loadPersonalAccessTokens() {
      if (!this.isDeveloperMenuVisible) {
        this.personalAccessTokens = [];
        return [];
      }

      const response = await apiClient.get("/user/personal-access-tokens");
      this.personalAccessTokens = Array.isArray(response?.data) ? response.data : [];
      return this.personalAccessTokens;
    },

    async createPersonalAccessToken(payload) {
      const response = await apiClient.post("/user/personal-access-tokens", payload);
      await this.loadPersonalAccessTokens();
      return response?.data || null;
    },

    async revokePersonalAccessToken(tokenId) {
      await apiClient.delete(`/user/personal-access-tokens/${tokenId}`);
      await this.loadPersonalAccessTokens();
    },

    async updateMySettings({ currentCompanyId, locale, theme }) {
      const payload = {
        currentCompanyId: currentCompanyId ?? null,
        locale: normalizeLocale(locale),
        theme: normalizeTheme(theme),
      };

      const response = await apiClient.put("/user/me/settings", payload);
      this.me = response?.data || null;

      if (this.me?.currentCompanyId) {
        localStorage.setItem(STORAGE_COMPANY_ID, this.me.currentCompanyId);
      } else {
        localStorage.removeItem(STORAGE_COMPANY_ID);
      }
      if (this.me?.locale) {
        localStorage.setItem(STORAGE_LOCALE, this.me.locale);
      }
      if (this.me?.theme) {
        localStorage.setItem(STORAGE_THEME, this.me.theme);
      }

      await this.refreshCompanyLogo();
      return this.me;
    },

    applyLocaleToI18n(i18n) {
      if (!i18n) return;
      const desired = this.me?.locale || localStorage.getItem(STORAGE_LOCALE) || null;
      if (!desired || !i18n.global) {
        return;
      }

      // vue-i18n can run in legacy mode (locale is a string) or composition mode (locale is a Ref).
      // Support both to avoid runtime crashes (blank page) after login.
      const global = i18n.global;
      const locale = global.locale;

      if (typeof locale === "string") {
        global.locale = desired;
        return;
      }

      if (locale && typeof locale === "object" && "value" in locale) {
        locale.value = desired;
        return;
      }

      // Fallback: try direct assignment.
      global.locale = desired;
    },

    applyTheme() {
      const theme = (this.me?.theme || localStorage.getItem(STORAGE_THEME) || null) || this.currentCompany?.defaultTheme || "EHW";
      const className = `theme-${String(theme).trim().toLowerCase()}`;

      const root = document.documentElement;
      const previous = Array.from(root.classList).filter((c) => c.startsWith("theme-"));
      previous.forEach((c) => root.classList.remove(c));
      root.classList.add(className);
    },

    async refreshCompanyLogo() {
      if (this.logoObjectUrl) {
        try {
          URL.revokeObjectURL(this.logoObjectUrl);
        } catch (_) {
          // ignore
        }
        this.logoObjectUrl = null;
      }

      const logoFileId = this.currentCompany?.logoFileId || null;
      if (!logoFileId) {
        return;
      }

      const response = await apiClient.get(`/file/download/${logoFileId}`, { responseType: "arraybuffer" });
      const contentType = response?.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      this.logoObjectUrl = URL.createObjectURL(blob);
    },
  },
});
